// eslint-disable-next-line no-restricted-imports
import { type SortDescriptor } from "@progress/kendo-data-query/dist/npm/sort-descriptor";
import { type FormikProps } from "formik";
import { type FormikTouched } from "formik/dist/types";
import { type ILineItemDefaults, LineItemType } from "legacyComponents/LineItemContainer.types";
import { isEqual, remove } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation, useRouteMatch } from "react-router";

import { IconPencilSimple } from "@btnet/blueprint-icons";
import { BTDropdown } from "@btnet/core-components";
import { cn } from "@btnet/utilities-core";

import { type BuilderInfo } from "helpers/AppProvider.types";
import { usePermission } from "helpers/globalContext/usePermission";

import { type BTSelectItem, BTServiceDropdownTypes } from "types/apiResponse/apiResponse";

import type yup from "utilities/form/yup";
import {
    confirmAndDeleteLineItem,
    getCostCodeSelectItemsFromLineItems,
    getTaxGroupSelectItemsFromLineItemDefaults,
    handleUpdateCostLineItems,
    type ICostCodeType,
    isLineItemValid,
    newLineItemDefaults,
    shouldBreakLinkToCostItem,
    sortMarkupLineItems,
} from "utilities/lineItem/lineItemHelper";
import { isInPortal } from "utilities/portal/portal";
import { routes } from "utilities/routes";
import {
    calculateTax,
    isTaxGroupIdValid,
    shouldDefaultToTaxable,
} from "utilities/tax/tax.utilities";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import { BTIconCaretSmallDown } from "commonComponents/btWrappers/BTIcon";
import { BTLinkRelative } from "commonComponents/btWrappers/BTLink/BTLink";
import { BTMenu } from "commonComponents/btWrappers/BTMenu/BTMenu";
import { BTPopover } from "commonComponents/btWrappers/BTPopover/BTPopover";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import {
    getMarkupLineItemPriceBreakdown,
    getPriceBreakdownWithNoTaxes,
} from "commonComponents/entity/lineItem/LineItemPriceBreakdown/LineItemPriceBreakdown.utility";
import { MarkupLineItemPriceBreakdown } from "commonComponents/entity/lineItem/LineItemPriceBreakdown/MarkupLineItemPriceBreakdown/MarkupLineItemPriceBreakdown";
import { RelatedItemType } from "commonComponents/entity/relatedItem/RelatedItem.types";
import { deleteActionCellDefaultConfig } from "commonComponents/utilities/LineItemContainer/cellImplementations/ActionCell/ActionCell";
import {
    costTypeCellDefaultConfig,
    type ICostTypeCellProps,
} from "commonComponents/utilities/LineItemContainer/cellImplementations/CostTypeCell";
import {
    type ILineItemRelatedItemsCellProps,
    LineItemRelatedItemsCell,
} from "commonComponents/utilities/LineItemContainer/cellImplementations/LineItemRelatedItemsCell";
import { marginCellDefaultConfig } from "commonComponents/utilities/LineItemContainer/cellImplementations/MarginCell";
import { getMarkupCellDefaultConfig } from "commonComponents/utilities/LineItemContainer/cellImplementations/MarkupCell";
import { ownerPriceCellDefaultConfig } from "commonComponents/utilities/LineItemContainer/cellImplementations/OwnerPriceCell";
import {
    type IQuantityCellProps,
    quantityCellDefaultConfig,
} from "commonComponents/utilities/LineItemContainer/cellImplementations/QuantityCell";
import {
    builderCostCellDefaultConfig,
    type IReadOnlyCurrencyProps,
    LineItemCurrencyFooter,
} from "commonComponents/utilities/LineItemContainer/cellImplementations/ReadOnlyCurrencyCell";
import { type ITaxesCellProps } from "commonComponents/utilities/LineItemContainer/cellImplementations/TaxesCell/TaxesCell";
import { getTaxesCellDefaultConfig } from "commonComponents/utilities/LineItemContainer/cellImplementations/TaxesCell/TaxesCell.utility";
import {
    type ITextAreaCellProps,
    TextAreaCell,
} from "commonComponents/utilities/LineItemContainer/cellImplementations/TextAreaCell";
import {
    type ITitleAndCostCodeProps,
    titleAndCostCodeCellDefaultConfig,
} from "commonComponents/utilities/LineItemContainer/cellImplementations/TitleAndCostCodeCell/TitleAndCostCodeCell";
import {
    getUnitCellDefaultConfig,
    type IUnitCellProps,
} from "commonComponents/utilities/LineItemContainer/cellImplementations/UnitCell";
import {
    type IUnitCostCellProps,
    unitCostCellDefaultConfig,
} from "commonComponents/utilities/LineItemContainer/cellImplementations/UnitCostCell";
import { LineItemContainer } from "commonComponents/utilities/LineItemContainer/LineItemContainer";
import { getRelatedEntityPermissionsRecord } from "commonComponents/utilities/LineItemContainer/lineItemContainer.utils";
import { GridLineItemColumnType } from "commonComponents/utilities/LineItemContainer/types/LineItem.enums";
import {
    type IGridLineItemActionRow,
    type ILineItemColumn,
    type ILineItemGroup,
    type ILineItemRow,
} from "commonComponents/utilities/LineItemContainer/types/LineItem.interfaces";
import {
    getDefaultActionRow,
    getLineItemData,
    type IMarkupLineItem,
} from "commonComponents/utilities/LineItemContainer/types/LineItem.types";

import { MarkupColumnTypes, ProposalDisplayColumnTypes } from "handlers";

import {
    CostCatalogViewType,
    type IAddFromCatalogRequest,
} from "entity/costCatalog/CostCatalog.types";
import { RouteCostCatalog } from "entity/costCatalog/RouteCostCatalog";
import { type ICostItemEntity } from "entity/costItem/CostItem/CostItem.api.types";
import { NewEntityId } from "entity/estimate/common/estimate.common.types";
import { SelectionOptionLineItem } from "entity/selectionChoice/SelectionChoice/SelectionChoice.api.types";
import { type ISelectionOptionLineItem } from "entity/selectionChoice/SelectionChoiceLineItemContainer/SelectionChoiceLineItemContainer.types";
import {
    type ISelectionChoiceOwnerPriceCellProps,
    SelectionChoiceOwnerPriceCell,
} from "entity/selectionChoice/SelectionChoiceLineItemContainer/SelectionChoiceOwnerPriceCell";
import { type ISelectionOptionEntity } from "entity/selectionOptions/SelectionOption/SelectionOption";
import { type ISelectionOptionFormValues } from "entity/selectionOptions/SelectionOption/SelectionOptionPresentational";
import { JobTaxVersion, TaxMethod } from "entity/tax/common/tax.types";
import { NoTaxId } from "entity/tax/TaxRate/TaxRate.api.types";

import "./SelectionOptionLineItemContainer.less";

const COLUMN_WIDTHS = {
    title: 300,
    unitCost: 150,
    unit: 90,
    clientPrice: 160,
} as const;

// Values define column sort order; not every value is used in every configuration
// (e.g., RelatedItem is only included when line items have related POs).
export enum SelectionOptionColumnOrder {
    TitleAndCostCode = 1,
    Description = 2,
    Quantity = 3,
    Unit = 4,
    UnitCost = 5,
    CostType = 6,
    BuilderCost = 7,
    Markup = 8,
    ClientPrice = 9,
    Margin = 10,
    Taxable = 11,
    InternalNotes = 12,
    RelatedItem = 13,
    Action = 14,
}

interface ISelectionOptionLineItemContainerProps extends FormikProps<ISelectionOptionFormValues> {
    builderInfo: BuilderInfo | null | undefined;
    canEdit: boolean;
    canEditCostCodes?: boolean;
    entity: ISelectionOptionEntity;
    isEditing: boolean;
    isCreating: boolean;
    jobId: number;
    lineItemDefaults: ILineItemDefaults;
    taxGroupId?: number;
    effectiveTaxVersion?: JobTaxVersion;
    validationSchema: yup.ObjectSchema<ISelectionOptionFormValues>;
    values: ISelectionOptionFormValues;
    onDeleteLineItems: (lineItems: SelectionOptionLineItem[]) => void;
    onLineItemShouldBreakLink?: (lineItemId: number, shouldBreak: boolean) => void;
    /**
     * Optional allowlist of columns to render. When provided, only columns whose
     * id is in the set will be shown (subject to the usual isHidden gating).
     * Use for callers (read-only catalog detail panel, etc.) that want a subset
     * of the full edit-mode column list.
     */
    visibleColumns?: ReadonlySet<SelectionOptionColumnOrder>;
}

export default function SelectionOptionLineItemContainer(
    props: Readonly<ISelectionOptionLineItemContainerProps>,
) {
    const history = useHistory();
    const location = useLocation();
    const match = useRouteMatch();
    const isClientViewMode = isInPortal({ owner: true });
    const canViewPurchaseOrders = usePermission("purchaseOrders", "canView");
    const canViewInvoices = usePermission("ownerInvoices", "canView");
    type GenericSelectItem = BTSelectItem<unknown>;
    const [costCodes, setCostCodes] = useState<GenericSelectItem[]>([]);
    const newLineItemIdRef = useRef(NewEntityId);
    const lineItemsFieldName = "selectionOptionLineItems";

    const { isEditing, values, setFieldValue } = props;
    const hasLineItems = (values.selectionOptionLineItems?.length ?? 0) > 0;

    const updateCostCodesByDefaults = useCallback(() => {
        setCostCodes([...getCostCodeSelectItemsFromLineItems(props.lineItemDefaults)]);
    }, [props.lineItemDefaults]);

    useEffect(() => {
        updateCostCodesByDefaults();
    }, [updateCostCodesByDefaults]);

    // Take line items out of edit mode when we exit edit mode
    useEffect(() => {
        if (!isEditing) {
            const hasItemsInEdit = values.selectionOptionLineItems?.some((li) => li._isInEdit);

            if (hasItemsInEdit) {
                const updatedLineItems =
                    values.selectionOptionLineItems?.map((li) => ({
                        ...li,
                        _isInEdit: false,
                    })) ?? [];
                setFieldValue(lineItemsFieldName, updatedLineItems);
            }
        }
    }, [isEditing, values.selectionOptionLineItems, setFieldValue, lineItemsFieldName]);

    const handleCostCodeListChange = useCallback((newCostCodes: GenericSelectItem[]) => {
        setCostCodes([...newCostCodes]);
    }, []);

    const getDefaultCostCode = useCallback((): number | null => {
        const costCodes = (props.lineItemDefaults.costCodeOptions?.value ??
            []) as (ICostCodeType & { selected?: boolean })[];
        return costCodes.find((cc) => cc.selected)?.id ?? null;
    }, [props.lineItemDefaults.costCodeOptions]);

    const getDefaultSelectionChoiceLineItem = useCallback(() => {
        return new SelectionOptionLineItem({
            ...newLineItemDefaults(
                newLineItemIdRef.current--,
                LineItemType.SelectionOption,
                null,
                getDefaultCostCode(),
                true,
            ),
            autoFocusedField: "titleAndCostCode",
            taxGroupId: shouldDefaultToTaxable(props.taxGroupId, props.effectiveTaxVersion)
                ? props.taxGroupId
                : NoTaxId,
        });
    }, [getDefaultCostCode, props.taxGroupId, props.effectiveTaxVersion]);

    const isLineItemDirty = useCallback(
        (lineItem: ILineItemRow) => {
            const initialLineItem = props.initialValues.selectionOptionLineItems?.find(
                (x) => x.id === lineItem.id,
            ) as SelectionOptionLineItem;
            return (
                initialLineItem === undefined ||
                !isEqual(getLineItemData(lineItem), getLineItemData(initialLineItem))
            );
        },
        [props.initialValues.selectionOptionLineItems],
    );

    const handleLineItemDeleted = useCallback(
        async (lineItem: SelectionOptionLineItem) => {
            const { values, setFieldValue } = props;
            const newLineItems = await confirmAndDeleteLineItem<SelectionOptionLineItem>(
                lineItem,
                values.selectionOptionLineItems ?? [],
                getDefaultSelectionChoiceLineItem(),
                "Selection Option",
            );
            setFieldValue(lineItemsFieldName, newLineItems);
            setFieldValue("hasDeletedLineItems", true);
        },
        [props, getDefaultSelectionChoiceLineItem, lineItemsFieldName],
    );

    const getColumnList = useCallback(
        (
            canEdit: boolean,
            costCodesParam: GenericSelectItem[],
            isEditing: boolean,
            jobId: number,
            hasLineItems: boolean,
            hasRelatedItems: boolean,
            canViewPOs: boolean,
            canViewInvoices: boolean,
            taxGroupId?: number,
            effectiveTaxVersion?: JobTaxVersion,
            visibleColumns?: ReadonlySet<SelectionOptionColumnOrder>,
        ) => {
            const canAndIsEditing = canEdit && isEditing;

            const isClientViewMode = hasLineItems && isInPortal({ owner: true });
            const permissionsRecord = getRelatedEntityPermissionsRecord(
                [
                    canViewPOs && RelatedItemType.PurchaseOrder,
                    canViewInvoices && RelatedItemType.CustomerInvoice,
                ].filter(Boolean) as RelatedItemType[],
            );

            // We will hide the extra columns when in view mode with no line items
            // The extra width isn't noticable when there are line items present
            const isEmptyViewMode = !hasLineItems && !isEditing;
            const columns = [
                {
                    ...titleAndCostCodeCellDefaultConfig(),
                    order: SelectionOptionColumnOrder.TitleAndCostCode,
                    minWidth: COLUMN_WIDTHS.title,
                    isSortable: true,
                    canEdit: () => canAndIsEditing,
                    getCellValue: ({
                        itemTitle,
                        costCodeId,
                        lineItemType,
                        costCodeTitle,
                        costItemId,
                    }: ISelectionOptionLineItem): ITitleAndCostCodeProps => ({
                        jobId,
                        lineItemType,
                        costItemId,
                        shouldShowLink: false,
                        costCodeProps: {
                            canEditCostCode: canAndIsEditing,
                            costCodeTitle,
                            costCodeId,
                            costCodes: costCodesParam,
                            costCodeEditing: {
                                canEdit: props.canEditCostCodes && isEditing,
                                onListChange: handleCostCodeListChange,
                                hasCostCodesFeature: false,
                            },
                        },
                        titleProps: {
                            canEditTitle: canAndIsEditing,
                            lineItemTitle: itemTitle,
                        },
                    }),
                },
                {
                    id: ProposalDisplayColumnTypes.Description,
                    name: "Description",
                    jsonKey: "description",
                    width: 300.0,
                    order: SelectionOptionColumnOrder.Description,
                    align: "left",
                    isSortable: false,
                    columnType: GridLineItemColumnType.textArea,
                    isFrozen: false,
                    isHidden: false,
                    canEdit: () => props.canEdit,
                    getCellValue: (row): ITextAreaCellProps => {
                        return {
                            text: row.description,
                            previewKey: "description",
                        };
                    },
                    Cell: TextAreaCell,
                } as ILineItemColumn<SelectionOptionLineItem, ITextAreaCellProps>,
                {
                    id: ProposalDisplayColumnTypes.InternalNotes,
                    name: "Internal Notes",
                    jsonKey: "internalNotes",
                    width: 300.0,
                    order: SelectionOptionColumnOrder.InternalNotes,
                    align: "left",
                    isSortable: false,
                    columnType: GridLineItemColumnType.textArea,
                    isFrozen: false,
                    isHidden: isEmptyViewMode || isClientViewMode,
                    canEdit: () => canEdit,
                    getCellValue: (row): ITextAreaCellProps => {
                        return {
                            text: row.internalNotes ?? undefined,
                            previewKey: "internalNotes",
                        };
                    },
                    Cell: TextAreaCell,
                } as ILineItemColumn<SelectionOptionLineItem, ITextAreaCellProps>,
                {
                    ...unitCostCellDefaultConfig(),
                    name: "Unit Cost",
                    width: COLUMN_WIDTHS.unitCost,
                    order: SelectionOptionColumnOrder.UnitCost,
                    isSortable: true,
                    isHidden: isClientViewMode,
                    canEdit: () => canAndIsEditing,
                    getCellValue: (row: ISelectionOptionLineItem): IUnitCostCellProps => ({
                        ...row,
                    }),
                },
                {
                    ...quantityCellDefaultConfig(),
                    order: SelectionOptionColumnOrder.Quantity,
                    isSortable: true,
                    canEdit: () => canAndIsEditing,
                    getCellValue: (row: ISelectionOptionLineItem): IQuantityCellProps => ({
                        ...row,
                    }),
                },
                {
                    ...getUnitCellDefaultConfig(),
                    width: COLUMN_WIDTHS.unit,
                    order: SelectionOptionColumnOrder.Unit,
                    isSortable: true,
                    canEdit: () => canAndIsEditing,
                    getCellValue: (row: ISelectionOptionLineItem): IUnitCellProps => ({
                        unit: row.unit,
                    }),
                },
                {
                    ...ownerPriceCellDefaultConfig(),
                    name: "Client Price",
                    width: COLUMN_WIDTHS.clientPrice,
                    order: SelectionOptionColumnOrder.ClientPrice,
                    isSortable: true,
                    isHidden: isEmptyViewMode && !isClientViewMode, // Always show in client build mode
                    getCellValue: (
                        row: ISelectionOptionLineItem,
                    ): ISelectionChoiceOwnerPriceCellProps => ({
                        ...row,
                        showSetPriceLater: false,
                        isTBD: row.isTBD,
                    }),
                    canEdit: (rowData: ISelectionOptionLineItem) =>
                        !rowData.isTBD && canAndIsEditing,
                    Cell: SelectionChoiceOwnerPriceCell,
                    getFooterValue: (
                        rowData: readonly (ISelectionOptionLineItem | IGridLineItemActionRow)[],
                    ) =>
                        rowData
                            .filter(
                                (li): li is ISelectionOptionLineItem =>
                                    (li as ISelectionOptionLineItem).costCodeId !== undefined,
                            )
                            .reduce((total, lineItem) => total + lineItem.ownerPrice, 0),
                },
                {
                    ...marginCellDefaultConfig(),
                    name: "Margin",
                    order: SelectionOptionColumnOrder.Margin,
                    isSortable: true,
                    isHidden: isEmptyViewMode || isClientViewMode,
                    getCellValue: (row: ISelectionOptionLineItem): IMarkupLineItem => row,
                    isDisabled: (rowData: ISelectionOptionLineItem) =>
                        rowData.isTBD || rowData.markupType === MarkupColumnTypes.OwnerPrice,
                    canEdit: () => canAndIsEditing,
                },
                {
                    ...costTypeCellDefaultConfig(),
                    order: SelectionOptionColumnOrder.CostType,
                    canEdit: () => canAndIsEditing,
                    getCellValue: (row: ISelectionOptionLineItem): ICostTypeCellProps => ({
                        costTypes: row.costTypes,
                        hasSingleSelectCostTypes:
                            props.lineItemDefaults.costTypes?.type ===
                            BTServiceDropdownTypes.SINGLESELECT,
                        markedAs: row.markedAs,
                    }),
                },
                {
                    ...builderCostCellDefaultConfig(),
                    order: SelectionOptionColumnOrder.BuilderCost,
                    isHidden: isClientViewMode,
                    getCellValue: ({
                        builderCost,
                    }: ISelectionOptionLineItem): IReadOnlyCurrencyProps => ({
                        value: builderCost,
                        formClassNames: ["text-bold"],
                    }),
                    getFooterValue: (
                        rowData: readonly (ISelectionOptionLineItem | IGridLineItemActionRow)[],
                    ) =>
                        rowData
                            .filter(
                                (li): li is ISelectionOptionLineItem =>
                                    (li as ISelectionOptionLineItem).costCodeId !== undefined,
                            )
                            .reduce((total, lineItem) => total + lineItem.builderCost, 0),
                },
                {
                    ...getMarkupCellDefaultConfig(),
                    order: SelectionOptionColumnOrder.Markup,
                    isHidden: isEmptyViewMode || isClientViewMode,
                    isDisabled: (rowData: ISelectionOptionLineItem) => rowData.isTBD,
                    canEdit: () => canAndIsEditing,
                    getCellValue: (row: ISelectionOptionLineItem): IMarkupLineItem => row,
                    getFooterValue: (
                        rowData: readonly (ISelectionOptionLineItem | IGridLineItemActionRow)[],
                    ) =>
                        rowData
                            .filter(
                                (li): li is ISelectionOptionLineItem =>
                                    (li as ISelectionOptionLineItem).costCodeId !== undefined,
                            )
                            .reduce((total, lineItem) => total + (lineItem.markupAmount ?? 0), 0),
                    FooterCell: LineItemCurrencyFooter,
                },
                ...(hasRelatedItems && !isClientViewMode
                    ? [
                          {
                              name: "Related items",
                              order: SelectionOptionColumnOrder.RelatedItem,
                              width: 200,
                              getCellValue: ({
                                  relatedItems,
                                  id,
                              }: ISelectionOptionLineItem): ILineItemRelatedItemsCellProps => ({
                                  relatedItems: relatedItems ?? [],
                                  lineItemId: id,
                                  jobId,
                                  permissionsRecord,
                              }),
                              Cell: LineItemRelatedItemsCell,
                          } as ILineItemColumn<
                              ISelectionOptionLineItem,
                              ILineItemRelatedItemsCellProps
                          >,
                      ]
                    : []),
                ...(canAndIsEditing
                    ? [
                          {
                              ...deleteActionCellDefaultConfig(handleLineItemDeleted),
                              order: SelectionOptionColumnOrder.Action,
                          } as const,
                      ]
                    : []),
            ];

            if (effectiveTaxVersion !== undefined && effectiveTaxVersion >= JobTaxVersion.V1) {
                const taxOptions = getTaxGroupSelectItemsFromLineItemDefaults(
                    props.lineItemDefaults,
                );
                const customProps = {
                    order: SelectionOptionColumnOrder.Taxable,
                    getCellValue: (row: ISelectionOptionLineItem) => ({
                        ...row,
                        newTaxGroupId: taxGroupId,
                        taxableAmount: row.ownerPrice,
                    }),
                    canEdit: () =>
                        canAndIsEditing && !isClientViewMode && isTaxGroupIdValid(taxGroupId),
                    isHidden: false,
                    getFooterValue: (
                        rowData: readonly (ISelectionOptionLineItem | IGridLineItemActionRow)[],
                    ) =>
                        rowData
                            .filter(
                                (li): li is ISelectionOptionLineItem =>
                                    (li as ISelectionOptionLineItem).costCodeId !== undefined &&
                                    isTaxGroupIdValid((li as ISelectionOptionLineItem).taxGroupId),
                            )
                            .reduce((total, li) => {
                                const rate =
                                    taxOptions.find((t) => t.value === li.taxGroupId)?.extraData
                                        ?.totalEffectiveRate ?? 0;
                                return total + calculateTax(li.ownerPrice, rate).toNumber();
                            }, 0),
                    FooterCell: LineItemCurrencyFooter,
                } as Partial<ILineItemColumn<ISelectionOptionLineItem, ITaxesCellProps>>;
                columns.push({
                    ...getTaxesCellDefaultConfig<ISelectionOptionLineItem>(),
                    ...customProps,
                } as (typeof columns)[number]);
            }

            // Cast the column list to the broad column type expected by the container.
            return columns.filter(
                (c) =>
                    !c.isHidden &&
                    (visibleColumns === undefined ||
                        visibleColumns.has(c.order as SelectionOptionColumnOrder)),
            ) as unknown as ILineItemColumn<ISelectionOptionLineItem, unknown, unknown>[];
        },
        [
            props.canEditCostCodes,
            props.lineItemDefaults,
            props.canEdit,
            handleCostCodeListChange,
            handleLineItemDeleted,
        ],
    );

    const hasRelatedItems = useMemo(
        () =>
            values.selectionOptionLineItems.some(
                (item) =>
                    (item as SelectionOptionLineItem).relatedItems &&
                    (item as SelectionOptionLineItem).relatedItems!.length > 0,
            ),
        [values.selectionOptionLineItems],
    );

    const columnsList = useMemo(
        () =>
            getColumnList(
                props.canEdit,
                costCodes,
                props.isEditing,
                props.jobId,
                hasLineItems,
                hasRelatedItems,
                canViewPurchaseOrders,
                canViewInvoices,
                props.taxGroupId,
                props.effectiveTaxVersion,
                props.visibleColumns,
            ),
        [
            getColumnList,
            props.canEdit,
            costCodes,
            props.isEditing,
            props.jobId,
            hasLineItems,
            hasRelatedItems,
            canViewPurchaseOrders,
            canViewInvoices,
            props.taxGroupId,
            props.effectiveTaxVersion,
            props.visibleColumns,
        ],
    );

    const handleLineItemSaved = useCallback(
        async (lineItemBeingSaved: ILineItemRow): Promise<ILineItemRow[]> => {
            const { values, setFieldValue } = props;
            const newItems =
                values.selectionOptionLineItems?.map((li) => {
                    if ((li as ISelectionOptionLineItem)._isInEdit) {
                        return { ...lineItemBeingSaved, _isInEdit: false, isExpanded: false };
                    }
                    return { ...li };
                }) ?? [];
            setFieldValue(lineItemsFieldName, newItems);

            const linkShouldBreak = await shouldBreakLinkToCostItem(
                lineItemBeingSaved as SelectionOptionLineItem,
                LineItemType.SelectionOption,
            );

            if (props.onLineItemShouldBreakLink) {
                props.onLineItemShouldBreakLink(lineItemBeingSaved.id, linkShouldBreak);
            }

            return newItems;
        },
        [props, lineItemsFieldName],
    );

    const handleLineItemAdded = useCallback(() => {
        const { values, setFieldValue } = props;
        const newItem = {
            ...getDefaultSelectionChoiceLineItem(),
            _isInEdit: true,
        } as SelectionOptionLineItem;
        setFieldValue(lineItemsFieldName, [
            ...(values.selectionOptionLineItems ?? []).map((li) => ({ ...li, _isInEdit: false })),
            newItem,
        ]);
    }, [props, getDefaultSelectionChoiceLineItem, lineItemsFieldName]);

    const handleSortChanged = useCallback(
        (sortOrder: SortDescriptor[]) => {
            const {
                values: { selectionOptionLineItems },
            } = props;
            const sort = sortOrder[0];

            const sortedList: IMarkupLineItem[] = sortMarkupLineItems(
                selectionOptionLineItems ?? [],
                parseInt(sort.field),
                sort.dir,
                costCodes,
            );

            props.setFieldValue(lineItemsFieldName, sortedList);
        },
        [props, costCodes, lineItemsFieldName],
    );

    const handleAddFromCostCatalog = useCallback(
        async (addFromCatalogRequest: IAddFromCatalogRequest) => {
            const {
                setFieldValue,
                values: { selectionOptionLineItems },
            } = props;
            const lineItemArray = selectionOptionLineItems;
            const newLineItems = addFromCatalogRequest.lineItems.map(
                (x) =>
                    new SelectionOptionLineItem({
                        ...x,
                        id: newLineItemIdRef.current--,
                    }),
            );
            remove(
                lineItemArray ?? [],
                (x: ISelectionOptionLineItem) => x.id <= 0 && !isLineItemDirty(x),
            );
            setFieldValue(lineItemsFieldName, [...(lineItemArray ?? []), ...newLineItems]);

            history.replace(location.pathname);
        },
        [props, isLineItemDirty, lineItemsFieldName, history, location.pathname],
    );

    const handleLineItemsTouched = useCallback(
        (lineItemTouched: FormikTouched<ISelectionOptionLineItem>[]) => {
            props.setTouched({
                ...props.touched,
                selectionOptionLineItems: lineItemTouched,
            });
        },
        [props],
    );

    const onUpdateCostLineItems = useCallback(
        (isBreakingCostItemLinks?: boolean | null, editedCostItem?: ICostItemEntity) => {
            if (editedCostItem) {
                const updatedLineItems = handleUpdateCostLineItems<ISelectionOptionLineItem>(
                    props.values.selectionOptionLineItems ?? [],
                    editedCostItem,
                    isBreakingCostItemLinks,
                );
                props.setFieldValue(lineItemsFieldName, updatedLineItems);
            }
        },
        [props, lineItemsFieldName],
    );

    const handleValidateLineItem = useCallback(
        async (index: number) => {
            // Narrow validation schema to the slice required by isLineItemValid generic
            const lineItemArraySchema = props.validationSchema as unknown as yup.ObjectSchema<{
                selectionOptionLineItems: SelectionOptionLineItem[];
            }>;
            return await isLineItemValid(
                lineItemArraySchema,
                (props.values.selectionOptionLineItems ?? [])[index] ??
                    ({} as SelectionOptionLineItem),
                "selectionOptionLineItems",
            );
        },
        [props.validationSchema, props.values.selectionOptionLineItems],
    );

    // Empty state row for when there are no line items but we want the table to show
    const emptyStateRow: IGridLineItemActionRow = useMemo(
        () => ({
            borderStyle: "none",
            id: "blankPlaceholder",
            isActionRow: true,
            onClick: undefined,
            rowText: "",
            defaultRowContent: isInPortal({ owner: true }) ? null : (
                <div className="defaultRowContent">
                    <span className="inline-text-with-icon">
                        No line items added yet. Use the <IconPencilSimple /> edit button at the top
                        to get started.
                    </span>
                </div>
            ),
            "aria-label": "No line items added yet. Use the edit button at the top to get started.",
        }),
        [],
    );

    const { canEdit, errors, jobId, setFieldTouched, touched } = props;

    const actionRow = getDefaultActionRow(handleLineItemAdded);
    const selectedLineItems = values.selectionOptionLineItems.filter((li) => li.isSelected);

    // Force LineItemContainer to remount when edit/content state changes to ensure proper scrollbar calculation
    // We don't want scrollbars for empty state when not editing, and we want to ensure they appear when
    // switching back and forth from edit mode to view mode
    const lineItemContainerKey = useMemo(() => {
        const editState = canEdit && isEditing ? "editing" : "viewing";
        const contentState = hasLineItems ? "withItems" : "empty";
        return `selectionOption-${editState}-${contentState}`;
    }, [canEdit, isEditing, hasLineItems]);

    const lineItemsWithActionRow = (() => {
        const isEditingMode = canEdit && isEditing;

        if (hasLineItems) {
            return isEditingMode
                ? ([...values.selectionOptionLineItems, actionRow] as (
                      | ISelectionOptionLineItem
                      | IGridLineItemActionRow
                  )[])
                : values.selectionOptionLineItems;
        } else {
            return isEditingMode
                ? ([actionRow] as IGridLineItemActionRow[])
                : ([emptyStateRow] as IGridLineItemActionRow[]);
        }
    })();

    return (
        <div
            className={cn("SelectionOptionLineItemContainer", {
                "has-line-items": hasLineItems,
                "is-editing": canEdit && isEditing,
            })}
            data-testid="SelectionOptionLineItemContainer"
        >
            <RouteCostCatalog
                modalConfig={{
                    parentRoute: "",
                    beforeClose: () => {
                        history.replace(match.url);
                    },
                }}
                addToEntityDisplayName="selection option"
                viewType={CostCatalogViewType.LineItemContainer}
                onAddCostItemLineItems={handleAddFromCostCatalog}
                jobIds={[jobId]}
                onUpdateParentLineItems={onUpdateCostLineItems}
                hasSingleSelectCostTypes={
                    props.lineItemDefaults.costTypes?.type === BTServiceDropdownTypes.SINGLESELECT
                }
            />
            <BTRow
                className="GridContainer-Toolbar padding-left-md"
                responsiveMode="viewport"
                justify="end"
                align="middle"
            >
                <BTCol>
                    {(props.isEditing || props.isCreating) && props.canEdit && (
                        <BTDropdown
                            overlay={
                                <BTMenu testid="checked-actions-menu">
                                    <BTMenu.Item
                                        key="deleteCheckedAction"
                                        testid="deleteCheckedAction"
                                        onClick={() => {
                                            props.onDeleteLineItems(selectedLineItems);
                                        }}
                                        className="padding-top-xxs"
                                    >
                                        Delete checked items
                                    </BTMenu.Item>
                                </BTMenu>
                            }
                            disabled={selectedLineItems.length === 0}
                            testid="checked-actions"
                        >
                            <BTPopover
                                content="No items selected"
                                disabled={selectedLineItems.length !== 0}
                            >
                                <BTButton
                                    data-testid="checked-actions-btn"
                                    icon={<BTIconCaretSmallDown />}
                                    type="secondary"
                                    disabled={selectedLineItems.length === 0}
                                >
                                    Checked actions
                                </BTButton>
                            </BTPopover>
                        </BTDropdown>
                    )}
                </BTCol>
                <BTCol>
                    {props.isEditing && (
                        <div className="flex">
                            <BTLinkRelative to={routes.costCatalog.addFromCatalog}>
                                <BTButton
                                    type="secondary"
                                    data-testid="addFromCatalog"
                                    className={cn(
                                        "margin-top-xs",
                                        "margin-bottom-xs",
                                        "margin-left-xs",
                                    )}
                                >
                                    Add from catalog
                                </BTButton>
                            </BTLinkRelative>
                        </div>
                    )}
                </BTCol>
            </BTRow>
            <LineItemContainer
                key={lineItemContainerKey}
                columnsList={columnsList}
                containerName="selectionOption"
                scrollable={true}
                sticky={false}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                setLineItemsTouched={handleLineItemsTouched}
                data={
                    lineItemsWithActionRow as unknown as (
                        | ILineItemRow
                        | ILineItemGroup<ILineItemRow>
                    )[]
                }
                isLineItemDirty={isLineItemDirty}
                onLineItemEditFinished={handleLineItemSaved}
                isEditable={canEdit && isEditing}
                touchedFields={touched.selectionOptionLineItems}
                hasSelectableRows={canEdit && isEditing && hasLineItems}
                lineItemErrors={errors.selectionOptionLineItems}
                canReorderRows={canEdit && isEditing}
                dataField={lineItemsFieldName}
                onSortChange={handleSortChanged}
                rounded
                onValidateLineItem={handleValidateLineItem}
            />
            <SelectionChoiceLineItemPriceBreakdown
                lineItems={values.selectionOptionLineItems ?? []}
                lineItemDefaults={props.lineItemDefaults}
                effectiveTaxVersion={props.effectiveTaxVersion}
                showBreakdownButton={!isClientViewMode && (isEditing || hasLineItems)}
            />
        </div>
    );
}

function SelectionChoiceLineItemPriceBreakdown({
    lineItems,
    lineItemDefaults,
    effectiveTaxVersion,
    showBreakdownButton,
}: Readonly<{
    lineItems: ISelectionOptionLineItem[];
    lineItemDefaults: ILineItemDefaults;
    effectiveTaxVersion?: JobTaxVersion;
    showBreakdownButton?: boolean;
}>) {
    const isTaxEnabled =
        effectiveTaxVersion !== undefined && effectiveTaxVersion >= JobTaxVersion.V1;
    const hasTaxableLineItems = useMemo(
        () => isTaxEnabled && lineItems.some((li) => isTaxGroupIdValid(li.taxGroupId)),
        [isTaxEnabled, lineItems],
    );
    const taxGroups = useMemo(
        () => getTaxGroupSelectItemsFromLineItemDefaults(lineItemDefaults),
        [lineItemDefaults],
    );
    const priceBreakdown = useMemo(
        () =>
            isTaxEnabled
                ? getMarkupLineItemPriceBreakdown(
                      false,
                      lineItems,
                      hasTaxableLineItems ? TaxMethod.Exclusive : TaxMethod.None,
                      undefined,
                      hasTaxableLineItems ? taxGroups : undefined,
                      undefined,
                  )
                : getPriceBreakdownWithNoTaxes(lineItems),
        [isTaxEnabled, hasTaxableLineItems, lineItems, taxGroups],
    );

    return (
        <div className="OwnerPriceBreakdown" data-testid="lineItemPricebreakdownContainer">
            <MarkupLineItemPriceBreakdown
                isFlatFee={false}
                priceBreakdown={priceBreakdown}
                priceTotalLabel="Total price"
                showBreakdown={isInPortal({ builder: true })}
                showBreakdownButton={showBreakdownButton}
                showPriceOnly={false}
                useUpdatedComponentWithTaxRates={isTaxEnabled}
                taxMethod={hasTaxableLineItems ? TaxMethod.Exclusive : undefined}
            />
        </div>
    );
}
